import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from "@/stores/use-auth-store.ts";
import type { ApiErrorResponse } from "@/types/auth.ts";
import * as Sentry from "@sentry/react";

// Admin API 클라이언트
const adminApiClient = axios.create({
  baseURL: import.meta.env.VITE_ADMIN_API_BASE_URL,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// User API 클라이언트 (인증용)
const userApiClient = axios.create({
  baseURL: import.meta.env.VITE_USER_API_BASE_URL,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

console.info('[ENV] VITE_ADMIN_API_BASE_URL =', import.meta.env.VITE_ADMIN_API_BASE_URL);
console.info('[ENV] VITE_USER_API_BASE_URL =', import.meta.env.VITE_USER_API_BASE_URL);

function setupInterceptors(client: AxiosInstance, clientName: string) {
  // 요청 인터셉터
  client.interceptors.request.use(
    (config) => {
      console.log(`🚀 ${clientName} API 요청:`, {
        url: config.url,
        method: config.method,
        baseURL: config.baseURL,
        data: config.data,
        params: config.params
      })

      const { accessToken, deviceId } = useAuthStore.getState();

      // Access Token 추가
      if (accessToken && !config.url?.includes('/auth/login')) {
        config.headers['X-Access-Token'] = accessToken;
      }

      // Device ID 추가
      if (deviceId) {
        config.headers['X-Device-ID'] = deviceId;
      }

      // FormData인 경우 Content-Type multipart로 설정
      // if (config.data instanceof FormData) {
      //   config.headers['Content-Type'] = 'multipart/form-data';
      // }

      return config
    },
    (error) => {
      console.error(`❌ ${clientName} 요청 에러:`, error)
      return Promise.reject(error)
    }
  )

  // 응답 인터셉터
  client.interceptors.response.use(
    (response) => {
      console.log(`✅ ${clientName} API 응답:`, {
        url: response.config.url,
        status: response.status,
        data: response.data
      })

      // 새로운 응답 구조: { code, message, data } → data만 추출
      if (
        response.data &&
        typeof response.data === 'object' &&
        'code' in response.data &&
        'data' in response.data
      ) {
        response.data = response.data.data
      }

      return response
    },
    async (error: AxiosError<ApiErrorResponse>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      const isLoginRequest = originalRequest.url?.includes('/auth/login');

      console.error(`❌ ${clientName} 응답 에러:`, {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });

      const status = error.response?.status;

      // Sentry Error Tracking
      Sentry.captureException(error, {
        tags: {
          error_type: 'api-error',
          api_endpoint: error.config?.url,
          http_status: status
        },
        contexts: {
          response: {
            status,
            data: error.response?.data
          },
          request: {
            url: error.config?.url,
            method: error.config?.method,
          },
        },
        level: status && status >= 500 ? 'error' : 'warning'
      });

      // 401 에러 처리
      if (
        status === 401 &&
        !originalRequest._retry &&
        !isLoginRequest
      ) {
        originalRequest._retry = true;
        useAuthStore.getState().clearTokens();
        window.location.href = '/login';
      }
      return Promise.reject(error)
    }
  )
}

setupInterceptors(adminApiClient, 'ADMIN');
setupInterceptors(userApiClient, 'USER');

export default adminApiClient;

export { userApiClient, adminApiClient };